import type { Auth, User } from "firebase/auth";
import { signOut } from "./signOut";
import { type FormEvent, useEffect, useRef, useState } from "react";
import * as api from "./api";
import { cycleWeeks, recommendedSize, volumes } from "./calculators";
import { callTime, emailName, shortTime } from "./calls";
import { DEAL_COLUMNS } from "./columns";
import {
  agreementText,
  type DealView,
  FIELDS,
  type FieldSpec,
  formChange,
  type FormValue,
  type FormValues,
  formValues,
  longDay,
  missingText,
  nextAction,
  openSteps,
  rebateShort,
  rebateText,
  sizeWarning,
  STATUS_TEXT,
  type Step,
  STEP_TEXT,
  STEPS,
  TERMS_FIELDS,
  viewLoad,
} from "./deals";
import { go, useCrumb, useTitle } from "./hooks";
import { isId } from "./route";
import { weekGroupLabel, WEEK_GROUPS } from "./plan";
import { type Saved, SERVER_PROBLEM, saveFailure } from "./save";
import { sortRows } from "./sort";
import { SortSelect, SortTh, useSort } from "./SortHeader";
import { memberName } from "./memberNames";
import { Skeleton } from "./Skeleton";

type Failure = "missing" | "server" | "network" | "session";

const failureOf = (e: unknown): Failure => {
  const code = e instanceof api.ApiError ? e.status : 0;
  return code === 404 ? "missing" : code === 401 ? "session" : code >= 500 ? "server" : "network";
};

function Problem({ failure, auth, retry }: { failure: Failure; auth: Auth; retry: () => void }) {
  if (failure === "session") {
    return (
      <p role="alert" className="error">
        Your session ended.{" "}
        <button type="button" onClick={() => signOut(auth)}>
          Sign in again
        </button>
      </p>
    );
  }
  return (
    <p role="alert" className="error">
      {failure === "server" ? SERVER_PROBLEM : "Cannot reach the server. Check your connection."}{" "}
      <button type="button" onClick={retry}>
        Try again
      </button>
    </p>
  );
}

/** "Start a deal" or "Open the deal" on the lead page (design 9.4). */
export function LeadDeal({ lead, detail, base, user }: { lead: api.Lead; detail: api.LeadDetail; base: string; user: User }) {
  const [saved, setSaved] = useState<Saved | null>(null);
  const [saving, setSaving] = useState(false);
  if (lead.account_id) {
    return (
      <p data-testid="lead-account">
        <a href={`${base}/accounts/${lead.account_id}`} onClick={go}>
          Open the account of this lead
        </a>
      </p>
    );
  }
  if (detail.deal_id) {
    return (
      <p>
        <a className="button" href={`${base}/deals/${detail.deal_id}`} onClick={go} data-testid="lead-open-deal">
          Open the deal
        </a>
      </p>
    );
  }
  if (!detail.can_deal) return null;
  const start = async () => {
    setSaving(true);
    setSaved(null);
    try {
      const made = await api.startDeal(await user.getIdToken(), lead.id, crypto.randomUUID());
      history.pushState(null, "", `${base}/deals/${made.deal.id}`);
      dispatchEvent(new PopStateEvent("popstate"));
    } catch (e) {
      setSaved(saveFailure(e, "The deal"));
      setSaving(false);
    }
  };
  return (
    <div className="actions">
      <button type="button" className="primary" data-testid="lead-start-deal" disabled={saving} onClick={() => void start()}>
        {saving ? "Starting…" : "Start a deal"}
      </button>
      {saved && !saved.ok && (
        <p role="alert" className="error">
          {saved.text}
        </p>
      )}
    </div>
  );
}

function DealTable({ deals, base, org }: { deals: api.Deal[]; base: string; org: api.Bootstrap }) {
  const [sort, setSort] = useSort("sort", DEAL_COLUMNS);
  return (
    <>
      <SortSelect id="deals-select" columns={DEAL_COLUMNS} sort={sort} onSort={setSort} fallback="Changed last first" />
      <div className="table-scroll" tabIndex={0} role="region" aria-label="Deals">
        <table className="list deals-table" data-testid="deal-table" data-cols="deals-select">
          <thead>
            <tr>
              {DEAL_COLUMNS.map((column) => (
                <SortTh key={column.key} column={column} sort={sort} onSort={setSort} />
              ))}
            </tr>
          </thead>
          <tbody>
            {sortRows(deals, DEAL_COLUMNS, sort).map((deal) => (
              <tr key={deal.id} data-testid="deal-row" data-status={deal.status}>
                <th scope="row">
                  <a href={`${base}/deals/${deal.id}`} onClick={go} data-testid="deal-link" title={deal.site_name || deal.lead_name}>
                    <span className="clip">{deal.site_name || deal.lead_name}</span>
                  </a>
                </th>
                <td data-label="Status">{STATUS_TEXT[deal.status]}</td>
                <td data-label="Salesperson" title={deal.owner_email ?? "No owner"}>
                  <span className="clip name">{deal.owner_email ? emailName(deal.owner_email) : "–"}</span>
                </td>
                <td data-label="State">{deal.state || "–"}</td>
                <td data-label="Rebate" title={rebateText(deal.rebate_method, deal.rebate_value)}>
                  {rebateShort(deal.rebate_method, deal.rebate_value)}
                </td>
                <td data-label="Outside limits" title={deal.reasons_now.join(" ")}>
                  {deal.reasons_now.length ? `Yes (${deal.reasons_now.length})` : "No"}
                </td>
                <td data-label="Changed" title={callTime(deal.updated_at, org.time_zone)}>
                  {shortTime(deal.updated_at, org.time_zone)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

type ListLoad = api.DealList | Failure | "hidden" | undefined;

export function DealList({ base, org, auth, user, canView }: { base: string; org: api.Bootstrap; auth: Auth; user: User; canView: boolean | undefined }) {
  const [view, setView] = useState<DealView>(() => {
    const asked = new URLSearchParams(location.search).get("view");
    return asked === "approve" || asked === "won" ? asked : "open";
  });
  const [loaded, setLoaded] = useState<{ view: DealView; load: ListLoad }>();
  const found = viewLoad(loaded, view);
  const [attempt, setAttempt] = useState(0);
  useTitle(`Deals · ${org.name}`);
  useEffect(() => {
    if (canView !== true) return;
    let live = true;
    setLoaded(undefined);
    user
      .getIdToken()
      .then((token) => api.deals(token, view))
      .then((list) => live && setLoaded({ view, load: list }))
      .catch((e) => {
        const failure = failureOf(e);
        if (live) setLoaded({ view, load: failure === "missing" ? "hidden" : failure });
      });
    return () => {
      live = false;
    };
  }, [user, view, attempt, canView]);
  if (canView === false || found === "hidden") {
    return (
      <main data-testid="deals-hidden">
        <h1 tabIndex={-1}>Deals</h1>
        <p>Your role does not show deals.</p>
      </main>
    );
  }
  const pick = (next: DealView) => {
    setView(next);
    const url = new URL(location.href);
    url.searchParams.set("view", next);
    history.replaceState(history.state, "", url);
  };
  const tabs: [DealView, string][] = [
    ["open", "Open"],
    ["approve", "Waits for approval"],
    ["won", "Won"],
  ];
  return (
    <main className="deals-page wide" data-testid="deals-page" data-view={found === undefined ? undefined : view}>
      <h1 tabIndex={-1}>Deals</h1>
      <p className="sub">A deal starts on the page of a lead. When the customer signs, the deal makes the account.</p>
      <div className="tabs" role="group" aria-label="Which deals">
        {tabs.map(([value, label]) => (
          <button key={value} type="button" aria-pressed={view === value} data-testid={`deals-${value}`} onClick={() => pick(value)}>
            {label}
          </button>
        ))}
      </div>
      {found === undefined ? (
        <Skeleton shape="table" columns={5} />
      ) : typeof found === "string" ? (
        <Problem failure={found} auth={auth} retry={() => setAttempt((n) => n + 1)} />
      ) : found.deals.length === 0 ? (
        <p data-testid="deals-none">{view === "approve" ? "No deal waits for an approval." : view === "won" ? "No won deals yet." : "No open deals."}</p>
      ) : (
        <>
          {found.more && <p className="sub">This list shows the deals that changed last.</p>}
          <DealTable deals={found.deals} base={base} org={org} />
        </>
      )}
    </main>
  );
}

function Field({
  field,
  value,
  onChange,
  disabled,
  problem,
  options,
}: {
  field: FieldSpec;
  value: FormValue;
  onChange: (next: FormValue) => void;
  disabled: boolean;
  problem: string | null;
  options?: Record<string, string>;
}) {
  const id = `deal-${field.key}`;
  const described = [problem ? `${id}-error` : "", field.hint ? `${id}-hint` : ""].filter(Boolean).join(" ") || undefined;
  const note = (
    <>
      {problem && (
        <p id={`${id}-error`} role="alert" className="error">
          {problem}
        </p>
      )}
      {field.hint && (
        <p id={`${id}-hint`} className="hint">
          {field.hint}
        </p>
      )}
    </>
  );
  if (field.kind === "check") {
    return (
      <div className="deal-field check">
        <label>
          <input type="checkbox" data-testid={id} checked={Boolean(value)} disabled={disabled} onChange={(e) => onChange(e.target.checked)} /> {field.label}
        </label>
        {note}
      </div>
    );
  }
  if (field.kind === "multi") {
    const chosen = Array.isArray(value) ? value : [];
    return (
      <fieldset className="deal-field multi" disabled={disabled}>
        <legend>{field.label}</legend>
        {Object.entries(options ?? {}).map(([key, label]) => (
          <label key={key}>
            <input
              type="checkbox"
              data-testid={`${id}-${key}`}
              checked={chosen.includes(key)}
              onChange={(e) => onChange(e.target.checked ? [...chosen, key] : chosen.filter((item) => item !== key))}
            />{" "}
            {label}
          </label>
        ))}
        {note}
      </fieldset>
    );
  }
  const common = {
    id,
    "data-testid": id,
    value: typeof value === "string" ? value : "",
    disabled,
    "aria-invalid": problem ? true : undefined,
    "aria-describedby": described,
  };
  let input;
  if (field.kind === "choice" || field.kind === "number-choice") {
    input = (
      <select {...common} onChange={(e) => onChange(e.target.value)}>
        <option value="">Choose…</option>
        {Object.entries(options ?? {}).map(([key, label]) => (
          <option key={key} value={key}>
            {label}
          </option>
        ))}
      </select>
    );
  } else if (field.kind === "note") {
    input = <textarea {...common} rows={3} onChange={(e) => onChange(e.target.value)} />;
  } else {
    const type = field.kind === "date" ? "date" : field.kind === "time" ? "time" : "text";
    const mode = ["int"].includes(field.kind) ? "numeric" : ["decimal", "percent", "rebate"].includes(field.kind) ? "decimal" : undefined;
    input = (
      <input
        {...common}
        type={type}
        inputMode={mode}
        maxLength={field.kind === "state" ? 2 : undefined}
        autoCapitalize={field.kind === "state" ? "characters" : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  return (
    <div className="deal-field">
      <label htmlFor={id}>{field.label}</label>
      {input}
      {note}
    </div>
  );
}

function Calculator({ values, detail, onUse }: { values: FormValues; detail: api.DealDetail; onUse: (rate: string, size: string) => void }) {
  const num = (key: string) => {
    const text = String(values[key] ?? "").trim();
    return text === "" || !Number.isFinite(Number(text)) ? null : Number(text);
  };
  const found = volumes(num("fryers"), num("changes_per_week"), num("fryer_lb"));
  const group = String(values.week_group ?? "");
  const weeks = cycleWeeks(group || null);
  const rate = num("rate_gal") ?? found?.raw ?? null;
  const size = rate && group ? recommendedSize(rate, weeks, detail.container_margin, detail.container_sizes) : null;
  return (
    <section className="calculator" aria-labelledby="deal-calculator" data-testid="deal-calculator">
      <h3 id="deal-calculator">Calculator</h3>
      {found ? (
        <dl className="facts">
          <dt>Fryer volume</dt>
          <dd data-testid="calc-fryer">{found.fryer} gal each week</dd>
          <dt>Collectable (after 25% cooking loss)</dt>
          <dd>{found.collectable} gal each week</dd>
          <dt>Raw (with 20% scrap)</dt>
          <dd data-testid="calc-raw">{found.raw} gal each week</dd>
        </dl>
      ) : (
        <p className="sub">Enter the fryers, the oil changes and the fryer size to get the volume.</p>
      )}
      {rate && !group && (
        <p className="sub" data-testid="calc-weeks">
          Choose the weeks of the visits above to get the container size.
        </p>
      )}
      {size && size.sizes.length > 0 && (
        <p data-testid="calc-size">
          For {weeks} {weeks === 1 ? "week" : "weeks"} between visits, the oil needs {size.need} gal with the margin:{" "}
          {size.sizes.length === 1 ? `one ${size.sizes[0]} gal container` : `two containers, ${size.sizes.join(" + ")} gal`}.
        </p>
      )}
      {found && (
        <button type="button" data-testid="calc-use" onClick={() => onUse(String(found.raw), size?.sizes.length === 1 ? String(size.sizes[0]) : "")}>
          Use the raw volume{size?.sizes.length === 1 ? " and the size" : ""}
        </button>
      )}
    </section>
  );
}

type DetailLoad = api.DealDetail | Failure | undefined;

export function DealPage({ id, base, org, auth, user, listAllowed = true }: { id: string; base: string; org: api.Bootstrap; auth: Auth; user: User; listAllowed?: boolean }) {
  const [found, setFound] = useState<DetailLoad>(undefined);
  const [attempt, setAttempt] = useState(0);
  const [step, setStep] = useState<Step>("site");
  const [values, setValues] = useState<FormValues>({});
  const [problem, setProblem] = useState<{ field: string; text: string } | null>(null);
  const [status, setStatus] = useState<Saved | null>(null);
  const [saving, setSaving] = useState(false);
  const [areas, setAreas] = useState<api.Area[]>([]);
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState("");
  const statusRef = useRef<HTMLParagraphElement>(null);
  const detail = typeof found === "object" ? found : null;
  const deal = detail?.deal ?? null;
  useTitle(deal ? `Deal: ${deal.site_name || deal.lead_name} · ${org.name}` : `Deal · ${org.name}`);
  useCrumb(deal ? deal.site_name || deal.lead_name : null);
  useEffect(() => {
    if (!isId(id)) {
      setFound("missing");
      return;
    }
    let live = true;
    user
      .getIdToken()
      .then((token) => api.deal(token, id))
      .then((next) => {
        if (!live) return;
        setFound(next);
        const open = openSteps(next.deal);
        if (next.deal.status === "approved") setStep("agreement");
        else if (next.deal.status === "submitted" && next.deal.can_approve) setStep("terms");
        else if (open.length && next.deal.can_edit) setStep(open[0][0]);
      })
      .catch((e) => live && setFound(failureOf(e)));
    user
      .getIdToken()
      .then(api.areas)
      .then((list) => live && setAreas(list), () => undefined);
    return () => {
      live = false;
    };
  }, [id, user, attempt]);
  useEffect(() => {
    if (deal) setValues(formValues(deal, step));
    setProblem(null);
  }, [deal, step]);
  const loaded = found !== undefined;
  useEffect(() => {
    if (loaded && document.activeElement === document.body) document.querySelector<HTMLElement>("main h1")?.focus();
  }, [loaded]);

  const back = (
    <p>
      <a href={listAllowed ? `${base}/deals` : base} onClick={go} className="back" data-testid="back-to-deals">
        {listAllowed ? "← All deals" : "← Home"}
      </a>
    </p>
  );
  if (found === undefined) {
    return (
      <main>
        {back}
        <Skeleton shape="detail" />
      </main>
    );
  }
  if (!detail || !deal) {
    return (
      <main data-testid={found === "missing" ? "deal-missing" : "deal-failed"}>
        {back}
        <h1 tabIndex={-1}>{found === "missing" ? "Deal not found" : "Cannot load the deal"}</h1>
        {found === "missing" ? <p>No deal at this link is visible to you.</p> : <Problem failure={found as Failure} auth={auth} retry={() => setAttempt((n) => n + 1)} />}
      </main>
    );
  }
  const show = (next: api.DealDetail, text: string) => {
    setFound(next);
    setStatus({ ok: true, text });
  };
  const run = async (work: () => Promise<void>, thing: string) => {
    setSaving(true);
    setStatus(null);
    try {
      await work();
    } catch (e) {
      setStatus(saveFailure(e, thing));
    } finally {
      setSaving(false);
      requestAnimationFrame(() => statusRef.current?.focus());
    }
  };
  const checked = () => {
    const result = formChange(deal, step, values, String(values.rebate_method ?? deal.rebate_method ?? ""));
    if ("field" in result) {
      setProblem(result);
      document.getElementById(`deal-${result.field}`)?.focus();
      return null;
    }
    setProblem(null);
    return result.change;
  };
  /** Save the step, then stay, go to the next step, or go to the step that the user chose. */
  const save = (event: FormEvent | null, then: "stay" | "next" | Step) => {
    event?.preventDefault();
    const change = checked();
    if (!change) return;
    const following = then === "next" ? STEPS[STEPS.indexOf(step) + 1] : then === "stay" ? undefined : then;
    const changed = Object.keys(change).length > 0;
    if (!changed && then !== "stay") {
      if (following) setStep(following);
      return;
    }
    void run(async () => {
      const next = changed ? await api.changeDeal(await user.getIdToken(), deal.id, change) : detail;
      show(next, changed ? `Saved: ${STEP_TEXT[step]}.` : "No change to save.");
      if (following) setStep(following);
    }, "The step");
  };
  const act = (kind: api.DealStepKind, done: string | ((next: api.DealDetail) => string), text = "") =>
    void run(async () => {
      const next = await api.stepDeal(await user.getIdToken(), deal.id, kind, text);
      show(next, typeof done === "string" ? done : done(next));
      setRejecting(false);
      setNote("");
    }, "The step");
  const choices = (field: FieldSpec): Record<string, string> | undefined => {
    if (field.key === "container_size") return Object.fromEntries(detail.container_sizes.map((size) => [String(size), `${size} gal`]));
    if (field.key === "area_id") return Object.fromEntries(areas.map((area) => [area.id, `${area.code} (week ${area.rotation_week})`]));
    if (field.key === "week_group") return Object.fromEntries(WEEK_GROUPS.map((group) => [group, weekGroupLabel(group)]));
    return field.options;
  };
  const visibleFields = FIELDS[step].filter((field) => field.key !== "rebate_value" || values.rebate_method);
  const rebateLabel = values.rebate_method === "percent" ? "Rebate (% of the market value)" : values.rebate_method === "flat" ? "Rebate ($ each period)" : "Rebate ($ per gal)";
  const editable = deal.can_edit && !saving;
  // A step button saves the typed values first, so that no value is lost.
  const goTo = (target: Step) => (target === step ? undefined : editable ? save(null, target) : setStep(target));
  const warning = sizeWarning(deal, detail.container_sizes, detail.container_margin);
  const winReady = deal.can_win && deal.unsigned.length === 0;
  const termsLocked = (key: string) => (TERMS_FIELDS as readonly string[]).includes(key) && !deal.can_edit_terms;
  return (
    <main className="deal-page" data-testid="deal-page" data-status={deal.status}>
      {back}
      <h1 tabIndex={-1}>Deal: {deal.site_name || deal.lead_name}</h1>
      <dl className="facts" data-testid="deal-facts">
        <dt>Status</dt>
        <dd data-testid="deal-status">{STATUS_TEXT[deal.status]}</dd>
        <dt>Next</dt>
        <dd data-testid="deal-next">{nextAction(deal)}</dd>
        <dt>Lead</dt>
        <dd>
          <a href={`${base}/leads/${deal.lead_id}`} onClick={go}>
            {deal.lead_name}
          </a>
          {deal.owner_email && <span className="sub"> · {memberName(deal.owner_email)}</span>}
        </dd>
        {deal.account_id && (
          <>
            <dt>Account</dt>
            <dd>
              <a href={`${base}/accounts/${deal.account_id}`} onClick={go} data-testid="deal-account">
                {deal.account_number} {deal.site_name}
              </a>
            </dd>
          </>
        )}
        {deal.approval_reasons.length > 0 && (
          <>
            <dt>Outside the limits</dt>
            <dd data-testid="deal-reasons">{deal.approval_reasons.join(" ")}</dd>
          </>
        )}
        {deal.decision_note && (
          <>
            <dt>{deal.status === "rejected" ? "Rejected" : "Decision"}</dt>
            <dd data-testid="deal-decision">
              {deal.decision_note}
              {deal.decided_email && <span className="sub"> · {memberName(deal.decided_email)}</span>}
            </dd>
          </>
        )}
      </dl>
      <p ref={statusRef} tabIndex={-1} role="status" data-testid="deal-status-line" className={status && !status.ok ? "error" : undefined}>
        {status?.text}
      </p>
      <nav aria-label="Steps of the deal" className="deal-steps">
        <ol>
          {STEPS.map((each, index) => {
            const left = deal.missing[each]?.length ?? 0;
            const unsignedStep = each === "agreement" && deal.unsigned.length > 0;
            return (
              <li key={each}>
                <button
                  type="button"
                  data-testid={`deal-step-${each}`}
                  aria-current={each === step ? "step" : undefined}
                  onClick={() => goTo(each)}
                  title={left ? `Missing: ${missingText(deal.missing[each])}` : undefined}
                >
                  {index + 1}. {STEP_TEXT[each]}
                  <span className={left || unsignedStep ? "step-mark open" : "step-mark"}>{left ? ` · ${left} missing` : unsignedStep ? " · not signed" : " · done"}</span>
                </button>
              </li>
            );
          })}
        </ol>
      </nav>
      <form className="deal-form" data-testid={`deal-form-${step}`} noValidate onSubmit={(event) => save(event, "next")}>
        <h2>{STEP_TEXT[step]}</h2>
        {deal.missing[step]?.length > 0 && <p className="sub">Needed before you send it: {missingText(deal.missing[step])}.</p>}
        {deal.status === "approved" && deal.can_edit && step !== "agreement" && (
          <p className="hint" data-testid="deal-approved-note">
            The terms are approved. A change in this step makes the deal a draft again, and you must send it again.
          </p>
        )}
        {deal.can_edit && !deal.can_edit_terms && (step === "terms" || step === "site") && (
          <p className="hint" data-testid="deal-terms-locked">
            Only a role with the contract grant sets the terms and the payee.
          </p>
        )}
        {step === "service" && warning && (
          <p className="hint" role="status" data-testid="deal-size-warning">
            {warning}
          </p>
        )}
        <div className="deal-fields">
          {visibleFields.map((field) => (
            <Field
              key={field.key}
              field={field.key === "rebate_value" ? { ...field, label: rebateLabel } : field}
              value={values[field.key] ?? ""}
              disabled={!editable || termsLocked(field.key)}
              problem={problem?.field === field.key ? problem.text : null}
              options={choices(field)}
              onChange={(next) => {
                // A new rebate method clears the value, so that 140 % never becomes $140 per gal.
                const cleared: FormValues = field.key === "rebate_method" && next !== values.rebate_method ? { rebate_value: "" } : {};
                setValues((before) => ({ ...before, [field.key]: next, ...cleared }));
                setStatus(null);
                if (problem?.field === field.key) setProblem(null);
              }}
            />
          ))}
        </div>
        {step === "volume" && (
          <Calculator
            values={values}
            detail={detail}
            onUse={(rate, size) => setValues((before) => ({ ...before, rate_gal: rate, ...(size ? { container_size: size } : {}) }))}
          />
        )}
        {step === "agreement" && (
          <p>
            <a href={`${base}/deals/${deal.id}/agreement`} onClick={go} data-testid="deal-agreement-link">
              Open the agreement to print
            </a>{" "}
            <span className="sub">Print it, get the signature, then enter the signer and the date here.</span>
          </p>
        )}
        {deal.can_edit && (
          <div className="actions">
            <button type="button" disabled={saving} data-testid="deal-save" onClick={(event) => save(event, "stay")}>
              {saving ? "Saving…" : "Save"}
            </button>
            {step !== "service" && (
              <button className={winReady ? undefined : "primary"} disabled={saving} data-testid="deal-save-next">
                Save and go on
              </button>
            )}
          </div>
        )}
      </form>
      <section aria-labelledby="deal-actions" className="deal-actions" data-testid="deal-actions">
        <h2 id="deal-actions">Send and approve</h2>
        {deal.can_submit && warning && (
          <p className="hint" data-testid="deal-size-warning-send">
            {warning}
          </p>
        )}
        {deal.can_submit && (
          <button
            type="button"
            className="primary"
            data-testid="deal-submit"
            disabled={saving || openSteps(deal).length > 0}
            onClick={() =>
              act("submit", (next) =>
                next.deal.status === "approved" ? "Sent and approved: the terms are inside the limits." : "Sent. A manager must approve the terms that are outside the limits.",
              )
            }
          >
            Send the deal
          </button>
        )}
        {deal.can_win && (
          <button type="button" className={winReady ? "primary" : undefined} data-testid="deal-win" disabled={saving || !winReady} onClick={() => act("win", "Done: the account, the contract, the setup order and the service plan are made.")}>
            Make the account
          </button>
        )}
        {deal.can_approve && (
          <dl className="facts" data-testid="deal-terms-summary">
            <dt>Rebate</dt>
            <dd>{rebateText(deal.rebate_method, deal.rebate_value)}</dd>
            <dt>Added consideration</dt>
            <dd>{deal.consideration ? `${deal.consideration}${deal.consideration_amount ? ` ($${deal.consideration_amount.toFixed(2)})` : ""}` : "None"}</dd>
            <dt>Term</dt>
            <dd>
              {deal.term_months ? `${deal.term_months} months` : "–"}
              {deal.auto_renew ? `, renews automatically, ${deal.notice_days} days notice` : ", no automatic renewal"}
            </dd>
            <dt>Stated rate</dt>
            <dd>{deal.rate_gal ? `${deal.rate_gal} gal each week` : "–"}</dd>
            <dt>Container</dt>
            <dd>{deal.container_size ? `${deal.container_size} gal` : "–"}</dd>
          </dl>
        )}
        {deal.can_approve && !rejecting && (
          <div className="actions">
            <button type="button" className="primary" data-testid="deal-approve" disabled={saving} onClick={() => act("approve", "Approved.")}>
              Approve the terms
            </button>
            <button type="button" data-testid="deal-reject-open" onClick={() => setRejecting(true)}>
              Reject…
            </button>
          </div>
        )}
        {rejecting && (
          <form
            className="deal-form"
            onSubmit={(event) => {
              event.preventDefault();
              act("reject", "Rejected.", note);
            }}
          >
            <label htmlFor="deal-reject-note">Why do you reject the terms?</label>
            <textarea id="deal-reject-note" data-testid="deal-reject-note" value={note} maxLength={1000} rows={3} onChange={(e) => setNote(e.target.value)} />
            <div className="actions">
              <button className="primary" data-testid="deal-reject" disabled={saving || !note.trim()}>
                Reject the terms
              </button>
              <button type="button" onClick={() => setRejecting(false)}>
                Cancel
              </button>
            </div>
          </form>
        )}
        {!deal.can_submit && !deal.can_win && !deal.can_approve && <p className="sub">{nextAction(deal)}</p>}
        {deal.can_submit && openSteps(deal).length > 0 && <p className="sub">Fill in each step first: {openSteps(deal).map(([each]) => STEP_TEXT[each]).join(", ")}.</p>}
        {deal.can_win && deal.unsigned.length > 0 && <p className="sub">Enter the signer and the date in the step Agreement first.</p>}
      </section>
    </main>
  );
}

/** The agreement to print, filled from the deal (design 9.4). The text is a synthetic sample. */
export function AgreementPage({ id, base, org, auth, user }: { id: string; base: string; org: api.Bootstrap; auth: Auth; user: User }) {
  const [found, setFound] = useState<DetailLoad>(undefined);
  const [attempt, setAttempt] = useState(0);
  const deal = typeof found === "object" ? found.deal : null;
  useTitle(deal ? `Agreement: ${deal.site_name} · ${org.name}` : `Agreement · ${org.name}`);
  useCrumb(deal ? `Agreement: ${deal.site_name || deal.lead_name}` : null);
  useEffect(() => {
    if (!isId(id)) {
      setFound("missing");
      return;
    }
    let live = true;
    user
      .getIdToken()
      .then((token) => api.deal(token, id))
      .then((next) => live && setFound(next))
      .catch((e) => live && setFound(failureOf(e)));
    return () => {
      live = false;
    };
  }, [id, user, attempt]);
  const back = (
    <p className="no-print">
      <a href={`${base}/deals/${id}`} onClick={go} className="back" data-testid="back-to-deal">
        ← The deal
      </a>
    </p>
  );
  if (found === undefined) {
    return (
      <main>
        {back}
        <Skeleton shape="detail" />
      </main>
    );
  }
  if (!deal) {
    return (
      <main data-testid="agreement-missing">
        {back}
        <h1 tabIndex={-1}>{found === "missing" ? "Agreement not found" : "Cannot load the agreement"}</h1>
        {found !== "missing" && <Problem failure={found as Failure} auth={auth} retry={() => setAttempt((n) => n + 1)} />}
      </main>
    );
  }
  return (
    <main className="agreement" data-testid="agreement-page">
      {back}
      <p className="no-print actions">
        <button type="button" className="primary" onClick={() => window.print()}>
          Print
        </button>
      </p>
      <h1 tabIndex={-1}>Used cooking oil collection agreement</h1>
      <p className="sub">Sample template · {org.name}</p>
      {agreementText(deal, org.name).map((paragraph) => (
        <p key={paragraph}>{paragraph}</p>
      ))}
      <div className="signatures">
        <div>
          <p>For the customer</p>
          <p className="line">{deal.signer_name || "Name"}</p>
          <p className="sub">
            {deal.signer_title || "Title"} · {deal.signed_on ? longDay(deal.signed_on) : "Date"}
          </p>
        </div>
        <div>
          <p>For {org.name}</p>
          <p className="line">Name</p>
          <p className="sub">Title · Date</p>
        </div>
      </div>
    </main>
  );
}
